import { useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { I18nProvider } from "@/lib/i18n";
import { ThemeProvider } from "@/lib/theme";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { SiteSettingsProvider } from "@/lib/site-settings";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Intro from "@/components/Intro";
import Index from "./pages/Index";
import Gallery from "./pages/Gallery";
import ArtworkDetails from "./pages/ArtworkDetails";
import About from "./pages/About";
import Auth from "./pages/Auth";
import Admin from "./pages/Admin";
import MyOrders from "./pages/MyOrders";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => {
  const [showIntro, setShowIntro] = useState(true);

  // Use session storage to show intro only once per session if desired, 
  // but the prompt says "make an intro" usually implying every refresh or initial load.
  // I'll keep it on every mount for now as per "production ready intro" standard, 
  // but if the user wants it once, we can use sessionStorage.

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <ThemeProvider>
          <AuthProvider>
            <CartProvider>
              <SiteSettingsProvider>
                <TooltipProvider>
                  {showIntro && <Intro onComplete={() => setShowIntro(false)} />}
                  <div className={`transition-opacity duration-1000 ${showIntro ? 'opacity-0' : 'opacity-100'}`}>
                    <Toaster />
                    <Sonner />
                    <BrowserRouter>
                      <Navbar />
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/gallery" element={<Gallery />} />
                        <Route path="/artwork/:id" element={<ArtworkDetails />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/admin" element={<Admin />} />
                        <Route path="/orders" element={<MyOrders />} />
                        <Route path="/cart" element={<Cart />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                      <Footer />
                    </BrowserRouter>
                  </div>
                </TooltipProvider>
              </SiteSettingsProvider>
            </CartProvider>
          </AuthProvider>
        </ThemeProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
};

export default App;
